import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  GraduationCap,
  Users,
  UserCheck,
  UserX,
  Wallet,
  CreditCard,
  Clock,
  AlertTriangle,
  Calendar,
  FileText,
  BookOpen,
  Award,
  Building2,
  Settings,
  Edit,
  BarChart3,
  ClipboardCheck,
  MessageSquare,
  UserPlus,
  Building,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { RoleDisplayNames } from '@/types/global.types';
import { useDashboardStats } from '@/shared/hooks/useDashboard';
import type { DashboardStat, QuickAction, RecentItem, Benchmark } from '@/types/dashboard.types';

// Icon mapping from string to Lucide component
const iconMap: Record<string, LucideIcon> = {
  GraduationCap,
  Users,
  UserCheck,
  UserX,
  Wallet,
  CreditCard,
  Clock,
  AlertTriangle,
  Calendar,
  FileText,
  BookOpen,
  Award,
  Building2,
  Settings,
  Edit,
  BarChart3,
  ClipboardCheck,
  MessageSquare,
  UserPlus,
  Building,
  TrendingUp,
  TrendingDown,
};

// Stat Card with benchmark support
interface StatCardProps {
  stat: DashboardStat;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ stat, onClick }) => {
  const Icon = iconMap[stat.icon] || GraduationCap;

  // Red highlight for changed values (since last page load)
  const valueColorClass = stat.hasChanged
    ? 'text-red-600'
    : 'text-gray-900';

  return (
    <div
      className={`
        bg-white rounded-xl border border-gray-200 p-6 shadow-sm
        ${stat.hasChanged ? 'ring-2 ring-red-200' : ''}
        ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">{stat.title}</p>
          <p className={`mt-2 text-3xl font-bold ${valueColorClass}`}>
            {stat.value}
          </p>
          {stat.benchmark && (
            <BenchmarkDisplay benchmark={stat.benchmark} />
          )}
        </div>
        <div className={`${stat.iconBgColor} p-3 rounded-lg flex-shrink-0`}>
          <Icon size={24} className={stat.iconColor} />
        </div>
      </div>
    </div>
  );
};

// Benchmark display component
interface BenchmarkDisplayProps {
  benchmark: Benchmark;
}

const BenchmarkDisplay: React.FC<BenchmarkDisplayProps> = ({ benchmark }) => {
  const isPositive = benchmark.trend === 'up';
  const isNeutral = benchmark.trend === 'stable';

  const TrendIcon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;
  const trendColor = isNeutral
    ? 'text-gray-500'
    : isPositive
    ? 'text-green-600'
    : 'text-red-600';

  return (
    <div className="mt-2 flex items-center gap-1 flex-wrap">
      <TrendIcon size={16} className={trendColor} />
      <span className={`text-sm font-medium ${trendColor}`}>
        {isPositive ? '+' : ''}
        {benchmark.changePercent}%
      </span>
      <span className="text-sm text-gray-500">{benchmark.label}</span>
    </div>
  );
};

// Quick Action Button
interface QuickActionButtonProps {
  action: QuickAction;
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ action }) => {
  const Icon = iconMap[action.icon] || FileText;

  return (
    <Link
      to={action.link}
      className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-gray-700"
    >
      <Icon size={24} />
      <span className="text-sm font-medium text-center">{action.label}</span>
    </Link>
  );
};

// Recent Item Row
interface RecentItemRowProps {
  item: RecentItem;
}

const RecentItemRow: React.FC<RecentItemRowProps> = ({ item }) => {
  const Icon = iconMap[item.icon || 'FileText'] || FileText;
  const formattedDate = new Date(item.timestamp).toLocaleDateString('fr-MA', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  const content = (
    <div className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50">
      <div className={`p-2 rounded-lg bg-gray-100`}>
        <Icon size={20} className={item.iconColor || 'text-gray-600'} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{item.title}</p>
        <p className="text-sm text-gray-500 truncate">{item.description}</p>
      </div>
      <span className="text-xs text-gray-400 whitespace-nowrap">{formattedDate}</span>
    </div>
  );

  if (item.link) {
    return <Link to={item.link}>{content}</Link>;
  }

  return content;
};

// Loading skeleton
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    <div className="bg-gray-200 rounded-xl h-24" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-gray-200 rounded-xl h-32" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-gray-200 rounded-xl h-64" />
      <div className="bg-gray-200 rounded-xl h-64" />
    </div>
  </div>
);

// Main Dynamic Dashboard Component
interface DynamicDashboardProps {
  title?: string;
  subtitle?: string;
  children?: React.ReactNode;
  /** Hide the "Activite recente" section (e.g. parent dashboard shows cards only) */
  showRecentActivity?: boolean;
  /**
   * Show an academic-year picker in the header (teaching roles). The figures
   * then describe the chosen year instead of always the live one — a professor
   * whose new year has no timetable yet can still consult the previous one.
   */
  academicYearSelector?: boolean;
}

export const DynamicDashboard: React.FC<DynamicDashboardProps> = ({
  title,
  subtitle,
  children,
  showRecentActivity = true,
  academicYearSelector = false,
}) => {
  const navigate = useNavigate();
  const { user, effectiveRole } = useAuth();
  const { tokens } = useAuthStore();
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  // undefined = the live year, resolved server-side.
  const [selectedYearId, setSelectedYearId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!academicYearSelector || !tokens?.accessToken) return;
    fetchAcademicYears(tokens.accessToken)
      .then(setAcademicYears)
      .catch(() => setAcademicYears([]));
  }, [academicYearSelector, tokens?.accessToken]);

  const currentYear = academicYears.find((y) => y.isCurrent);
  // ALWAYS send an explicit year once the list is known. "Current" is computed
  // from today's date by the planning endpoint, but every other service reads
  // the stored is_current column — and the two disagree whenever that column
  // has not been rolled over to the new year. Sending nothing let the backend
  // fall back to the stale column, so picking the current year silently showed
  // the previous one's figures.
  const effectiveYearId = selectedYearId ?? currentYear?.id;
  const isPastYear = !!effectiveYearId && !!currentYear && effectiveYearId !== currentYear.id;

  const { data, isLoading, error, refetch, isFetching } = useDashboardStats(effectiveYearId);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle size={48} className="mx-auto text-red-400 mb-4" />
        <p className="text-red-700 font-medium">Erreur de chargement du tableau de bord</p>
        <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const { stats = [], quickActions = [], recentItems = [] } = data || {};

  // Teaching dashboards describe a timetable, not a feed: today's sessions and
  // this week's are their own panels, and "Activité récente" is dropped for
  // them — it only ever showed "aucune activité" anyway.
  const todaySessions = recentItems.filter((item) => item.type === 'today-session');
  const weekSessions = recentItems.filter((item) => item.type === 'week-session');
  const otherRecentItems = recentItems.filter(
    (item) => item.type !== 'today-session' && item.type !== 'week-session'
  );
  const teaches = !!user?.isAlsoProfessor || effectiveRole === 'TEACHER';

  const handleStatClick = (stat: DashboardStat) => {
    if (!stat.link) return;
    // Carry the year through the click: landing on Mes Cours in the CURRENT
    // year after clicking a card that counted a closed one silently changes
    // the subject under the user.
    if (isPastYear && effectiveYearId) {
      const separator = stat.link.includes('?') ? '&' : '?';
      navigate(`${stat.link}${separator}academicYearId=${effectiveYearId}`);
      return;
    }
    navigate(stat.link);
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{title || 'Tableau de bord'}</h1>
            <p className="text-primary-100 mt-1">
              {subtitle || user?.fonction || (effectiveRole && RoleDisplayNames[effectiveRole]) || ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {academicYearSelector && academicYears.length > 0 && (
              <select
                value={effectiveYearId ?? ''}
                onChange={(e) => setSelectedYearId(e.target.value || undefined)}
                className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 hover:bg-white/20 transition-colors [&>option]:text-gray-900"
                title="Année académique"
              >
                {academicYears.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name}
                    {year.isCurrent ? ' (en cours)' : ''}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
              title="Actualiser"
            >
              <RefreshCw size={20} className={isFetching ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {isPastYear && (
          <p className="mt-3 text-sm text-amber-100 bg-amber-500/20 rounded-lg px-3 py-2">
            Vous consultez une année clôturée. Les chiffres ci-dessous décrivent cette année-là.
          </p>
        )}
      </div>

      {/* Stats Grid */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <StatCard
              key={stat.id}
              stat={stat}
              onClick={stat.link ? () => handleStatClick(stat) : undefined}
            />
          ))}
        </div>
      )}

      {/* Main Content - 3 columns: Mes Séances (for teaching staff) | Activité récente | Actions rapides */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mes séances aujourd'hui — for anyone who teaches. Hidden on a closed
            year: "today" does not exist inside it, so the panel could only ever
            be empty and would read as a bug. */}
        {teaches && !isPastYear && (
          <div className="bg-gradient-to-b from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
            <div className="px-6 py-4 border-b border-blue-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar size={20} className="text-blue-600" />
                <h2 className="font-semibold text-blue-900">Mes séances aujourd&apos;hui</h2>
              </div>
              {todaySessions.length > 0 && (
                <span className="text-sm font-medium text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                  {todaySessions.length} aujourd'hui
                </span>
              )}
            </div>
            {todaySessions.length > 0 ? (
              <div className="divide-y divide-blue-100">
                {todaySessions.slice(0, 5).map((item) => (
                  <RecentItemRow key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center">
                <Calendar size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">Aucune séance prévue aujourd&apos;hui</p>
              </div>
            )}
          </div>
        )}

        {/* Mes séances cette semaine — the week at a glance, grouped by day */}
        {teaches && !isPastYear && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar size={20} className="text-gray-500" />
                <h2 className="font-semibold text-gray-900">Mes séances cette semaine</h2>
              </div>
              {weekSessions.length > 0 && (
                <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                  {weekSessions.length} séance{weekSessions.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {weekSessions.length > 0 ? (
              <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {weekSessions.map((item) => (
                  <RecentItemRow key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center">
                <Calendar size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">Aucune séance cette semaine</p>
              </div>
            )}
          </div>
        )}

        {/* Activité récente — not for teaching dashboards, which show the
            timetable panels above instead. */}
        {showRecentActivity && !teaches && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm lg:col-span-2">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Activite recente</h2>
            </div>
            {otherRecentItems.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {otherRecentItems.map((item) => (
                  <RecentItemRow key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center">
                <Clock size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">Aucune activite recente</p>
              </div>
            )}
          </div>
        )}

        {/* Actions rapides */}
        {quickActions.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Actions rapides</h2>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((action) => (
                <QuickActionButton key={action.id} action={action} />
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Additional Content */}
      {children}
    </div>
  );
};

export default DynamicDashboard;
